import React, { useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { paypalClientId, paypalCurrency } from './helpers/paypalConfig';
import { usePayPalButtons } from './helpers/usePayPalButtons';
import { usePayPalSdk } from './helpers/usePayPalSdk';
import { isValidGuestEmail } from '../../hooks/shippingUpdate.helpers';
import './paypalExpressButton.scss';

const hasConfirmedShippingAddress = (shipping?: {
  firstName?: string | null;
  lastName?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
  telephone?: string | null;
} | null) =>
  Boolean(
    shipping?.firstName?.trim() &&
    shipping?.lastName?.trim() &&
    shipping?.addressLine1?.trim() &&
    shipping?.city?.trim() &&
    shipping?.postcode?.trim() &&
    shipping?.country?.trim() &&
    shipping?.telephone?.trim()
  );

const PayPalExpressButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(state => state.user.user);
  const userShipping = useAppSelector(state => state.user.shipping);
  const guestEmail = useAppSelector(state => state.shoppingCart.guestEmail);
  const guestShipping = useAppSelector(state => state.shoppingCart.guestShippingAddress);
  const cartItems = useAppSelector(state => state.shoppingCart.cartItems);
  const shipping = user?.id ? userShipping : guestShipping;
  const isGuestCheckout = !user?.id;
  const hasGuestEmail = !isGuestCheckout || isValidGuestEmail(guestEmail);
  const hasShipping = hasConfirmedShippingAddress(shipping);
  const checkoutReadyForPayPal = hasGuestEmail && hasShipping;
  const activePayPalClientId = checkoutReadyForPayPal ? paypalClientId : undefined;
  const paypalContainerRef = useRef<HTMLDivElement | null>(null);
  const paypalSdkStatus = usePayPalSdk(activePayPalClientId, paypalCurrency);
  const [paypalError, setPayPalError] = useState<string | null>(null);
  const [paypalProcessing, setPayPalProcessing] = useState(false);

  usePayPalButtons({
    dispatch,
    navigate,
    paypalClientId: activePayPalClientId,
    paypalContainerRef,
    paypalSdkStatus,
    setPayPalError,
    setPayPalProcessing,
    shipping,
    userId: user?.id,
    guestEmail: user?.id ? undefined : guestEmail,
    guestCartItems: user?.id ? undefined : cartItems,
  });

  if (!paypalClientId) {
    return null;
  }

  if (!checkoutReadyForPayPal) {
    return (
      <div className="paypal-express">
        <Alert
          type="info"
          showIcon
          message="Complete delivery details before PayPal checkout."
          description={
            hasGuestEmail
              ? 'Confirm the shipping address first so the order can be fulfilled after payment.'
              : 'Enter a valid guest email and confirm the shipping address first.'
          }
        />
      </div>
    );
  }

  return (
    <div className="paypal-express">
      <div className="paypal-express__divider">
        <span>Or pay with PayPal</span>
      </div>

      {paypalSdkStatus === 'loading' && (
        <div className="paypal-express__loading">
          <Spin size="small" />
        </div>
      )}

      <div
        ref={paypalContainerRef}
        className={`paypal-express__button ${
          paypalSdkStatus !== 'ready' || paypalProcessing ? 'is-hidden' : ''
        }`}
      />

      {paypalProcessing && (
        <div className="paypal-express__loading">
          <Spin size="small" />
        </div>
      )}

      {paypalSdkStatus === 'error' && (
        <Alert type="error" showIcon message="Failed to load PayPal." />
      )}

      {paypalError && <Alert type="error" showIcon message={paypalError} />}
    </div>
  );
};

export default PayPalExpressButton;
